import { Box } from '@mui/material';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import SlotAssetsDetails from '../edit-details/slot-assets-details';
import { getSlotAssetsDetailsTitle } from '../edit-details/slot-assets-details-title';
import EditDetailsPanel from '../edit-details-panel';
import { getScheduleInlineSlotTitle } from '../edit-details/schedule-slot-inline-details-title';
import { flowControlInlinePerformMiddleWrapperSx } from '../flow-control-perform-expand';
import { useTranslations } from '@/i18n/use-translations';
import type { ScheduleInlineDetailsTab } from '../edit-details/schedule-inline-details-tab';
import type { Mode } from '@/types/entity-mode';

type Variant = 'slotAssets' | 'schedule';

/**
 * Narrow viewport: panel under a slot row (slot assets or schedule details).
 * Shared by timed and untimed sections so layout stays aligned.
 */
export function SlotInlineDetailsBelowRow({
  slot,
  activeSlotId,
  show,
  variant,
  flowStartOffset,
  eventSlotIdParam,
  onWideScreenSlotDeletedFromList,
  onSessionPublishSuccess,
  stackMarginX = 0,
  inlineScheduleDetailsTab,
  slotAssetsListViewMode,
}: {
  slot: FlowSlotDto;
  activeSlotId: string | null;
  show: boolean;
  variant: Variant | undefined;
  flowStartOffset: number;
  eventSlotIdParam?: string | null;
  onWideScreenSlotDeletedFromList?: () => void;
  onSessionPublishSuccess?: () => void;
  /** Match timed-section (`0`) vs legacy untimed list inset (`-1`) if needed */
  stackMarginX?: number;
  inlineScheduleDetailsTab?: ScheduleInlineDetailsTab;
  slotAssetsListViewMode?: Mode;
}) {
  const t = useTranslations();
  if (!show || !variant || activeSlotId !== slot.id) return null;

  const commonSx = {
    ...flowControlInlinePerformMiddleWrapperSx,
    mx: stackMarginX,
    bgcolor: 'background.paper',
    px: 1,
    py: 1.5,
  };

  if (variant === 'schedule') {
    return (
      <Box sx={commonSx} data-slot-inline-details data-slot-id={slot.id}>
        <EditDetailsPanel
          slot={slot}
          slotTitle={getScheduleInlineSlotTitle(slot, flowStartOffset, t)}
          onSlotDeleted={onWideScreenSlotDeletedFromList}
          onSessionPublishSuccess={onSessionPublishSuccess}
          eventSlotIdParam={eventSlotIdParam}
          variant="inline"
          inlineScheduleDetailsTab={inlineScheduleDetailsTab}
        />
      </Box>
    );
  }

  return (
    <Box sx={commonSx} data-slot-inline-details data-slot-id={slot.id}>
      <SlotAssetsDetails
        selectedSlot={slot}
        slotTitle={getSlotAssetsDetailsTitle(slot)}
        listViewMode={slotAssetsListViewMode}
      />
    </Box>
  );
}
